import React, { useEffect, useState } from "react";
import { TextInput } from "../../components/form/inputs";
import ToggleBtn from "../../components/button/ToggleBtn";
import { useNavigate } from "react-router-dom";
import PrimaryBtn from "../../components/button/PrimaryBtn";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import {
  addPickUpAddress,
  getAddressDetails,
} from "../../redux/thunks/setAddressThunk";
import toast from "react-hot-toast";
import DecorativeSquares from "../../components/ui/DecorativeSquares";
import AddressShimmer from "../../components/Preloader/AddressShimmer";
import { FaArrowLeft } from "react-icons/fa";
// HELPER FUNCTION:
const extractAddress = (place) => {
  const address = {
    country: "",
    city: "",
    province: "",
    pincode: "",
    fullAddress: place.formatted_address || "",
    addressLine2: "",
  };

  let sublocality = "";
  let locality = "";
  let route = "";

  place.address_components.forEach((component) => {
    const types = component.types;

    if (types.includes("country")) address.country = component.long_name;
    if (types.includes("administrative_area_level_1"))
      address.province = component.long_name;
    if (types.includes("locality")) {
      address.city = component.long_name;
      locality = component.long_name;
    }
    if (types.includes("sublocality") || types.includes("sublocality_level_1"))
      sublocality = component.long_name;
    if (types.includes("route")) route = component.long_name;
    if (types.includes("postal_code")) address.pincode = component.long_name;
  });

  // 🔥 Short nearby address (priority wise)
  address.addressLine2 =
    sublocality || route || locality || address.province || "";

  return address;
};

const UpdatePickUpAddress = () => {
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState();
  const dispatch = useDispatch();
  const { isAddressLoading } = useSelector((state) => state.pickUpAdd);
  const navigate = useNavigate();

  const [address, setAddress] = useState({
    fullAddress: "",
    addressLine2: "",
    country: "",
    city: "",
    state: "",
    pincode: "",
    lat: null,
    lng: null,
  });

  const handleGoBack = () => {
    navigate(-1);
  };

  const [deliveryRange, setDeliveryRange] = useState("");
  const [deliverPrice, setDeliveryPrice] = useState("");

  const mapContainerStyle = {
    width: "100%",
    height: "300px",
  };

  const center = {
    lat: 20.5937,
    lng: 78.9629,
  };

  const [mapCenter, setMapCenter] = useState(center);
  const [markerPosition, setMarkerPosition] = useState(null);
  const autocompleteRef = React.useRef(null);
  const currencySymbol = localStorage.getItem("currency");
  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const extracted = extractAddress(place);

    setAddress((prev) => ({
      ...prev,
      ...extracted,
      lat,
      lng,
    }));

    setMapCenter({ lat, lng });
    setMarkerPosition({ lat, lng });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  //   Get address details to store in the value
  useEffect(() => {
    dispatch(getAddressDetails())
      .unwrap()
      .then((res) => {
        // console.log("The res is :", res.data);
        const data = res.data;

        setAddress({
          fullAddress: data?.address_1 || "",
          addressLine2: data?.address_2 || "",
          country: data?.country || "",
          city: data?.city || "",
          state: data?.province || "",
          pincode: data?.pin_code || "",
          lat: data?.latitude || null,
          lng: data?.longitude || null,
        });

        // 🔥 MAP + MARKER SYNC
        if (data?.latitude && data?.longitude) {
          const latLng = {
            lat: Number(data.latitude),
            lng: Number(data.longitude),
          };

          setMapCenter(latLng);
          setMarkerPosition(latLng);
        }

        setIsDeliveryEnabled(Number(data?.is_home_delivery) === 1 ? 1 : 0);
        setDeliveryRange(data?.delivery_upto || "");
        setDeliveryPrice(data?.delivery_charges || "");
      });
  }, [dispatch]);

  const onAddressUpdate = async () => {
    try {
      const payload = {
        address_1: address.fullAddress,
        address_2: address.addressLine2,
        country: address.country,
        city: address.city,
        province: address.state,
        pin_code: address.pincode,
        latitude: address.lat,
        longitude: address.lng,
        is_home_delivery: isDeliveryEnabled,
        delivery_upto: deliveryRange,
        delivery_charges: deliverPrice,
      };
      //   console.log("The payload data is :", payload);

      const res = await dispatch(addPickUpAddress(payload)).unwrap();

      if (res.success) {
        toast.success(res.message || "Address Added Successfully");
        navigate("/profile");
      } else {
        toast.error(res.message || "Address Failed");
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  return (
    <section className="relative min-h-screen max-w-7xl mx-auto px-4 py-6">
      <DecorativeSquares />
      <div className="sticky top-2 z-10 bg-white/80 backdrop-blur-md rounded-2xl shadow-md px-4 py-3 flex items-center justify-between mb-6">
        <button
          onClick={handleGoBack}
          className="flex items-center p-2 rounded-full hover:bg-gray-100"
        >
          <FaArrowLeft size={20} />
        </button>

        <h1 className="mx-auto text-xl md:text-2xl font-semibold text-gray-900">
          Update your pickup information
        </h1>
      </div>

      {isAddressLoading ? (
        <AddressShimmer />
      ) : (
        <>
          <div className="max-w-7xl mx-auto space-y-8 bg-white border border-gray-100 rounded-xl p-4 md:p-6">
            {/* Header */}

            <div className="w-full mt-4">
              <p className="text-sm font-medium text-gray-700 mb-1">
                Choose location{" "}
                <span className="text-red-500 ml-1">*</span>{" "}
              </p>

              <Autocomplete
                onLoad={(ref) => (autocompleteRef.current = ref)}
                onPlaceChanged={onPlaceChanged}
              >
                <input
                  type="text"
                  placeholder="Search location"
                  name="fullAddress"
                  value={address.fullAddress}
                  onChange={handleChange}
                  className="w-full px-4 py-2 mb-3 border rounded-lg outline-none"
                />
              </Autocomplete>
            </div>
            {/* ================= Address Form ================= */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
              {/* <TextInput
            label="Pickup Address"
            name="fullAddress"
            value={address.fullAddress}
            onChange={handleChange}
          /> */}

              <TextInput
                label="Address Line 2"
                required={true}
                name="addressLine2"
                value={address.addressLine2}
                onChange={handleChange}
              />

              <TextInput
                required={true}
                label="Country"
                name="country"
                value={address.country}
                onChange={handleChange}
              />

              <TextInput
                required={true}
                label="City"
                name="city"
                value={address.city}
                onChange={handleChange}
              />

              <TextInput
                required={true}
                label="Province / State"
                name="state"
                value={address.state}
                onChange={handleChange}
              />

              <TextInput
                required={true}
                label="Pin Code"
                name="pincode"
                value={address.pincode}
                onChange={handleChange}
              />
            </div>

            {/* ================= Google Map ================= */}
            <div className="space-y-2">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={15}
                center={mapCenter}
              >
                {markerPosition && <Marker position={markerPosition} />}
              </GoogleMap>
              {address.city && (
                <p className="text-sm text-gray-600 mt-1">
                  📍 {address.city}
                  {address.state ? `, ${address.state}` : ""}
                  {address.country ? `, ${address.country}` : ""}
                </p>
              )}
            </div>

            {/* ================= Delivery Toggle ================= */}
            <div className="flex items-center justify-between max-w-md">
              <p className="text-base text-[#191919] font-medium">
                Do you offer home delivery?
              </p>
              <ToggleBtn
                isOn={isDeliveryEnabled === 1}
                size={40}
                onChange={(val) => setIsDeliveryEnabled(val ? 1 : 0)}
              />
            </div>

            {/* ================= Delivery Options (Conditional) ================= */}
            {isDeliveryEnabled ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
                {/* Deliver Range */}
                <div className="border border-[#E5E5E5] rounded-xl p-4 bg-gray-50 space-y-2">
                  <p className="text-sm font-medium text-[#191919]">
                    Deliver up to
                  </p>
                  <div className="flex items-center">
                    <input
                      type="number"
                      placeholder="10"
                      value={deliveryRange}
                      onChange={(e) => setDeliveryRange(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-l-md outline-none text-sm"
                    />
                    <span className="px-3 py-2 bg-[#FFEFEB] border border-l-0 border-[#FFCCBF] rounded-r-md text-sm">
                      KM
                    </span>
                  </div>
                </div>

                {/* Delivery Price */}
                <div className="border border-[#E5E5E5] rounded-xl p-4 bg-gray-50 space-y-2">
                  <p className="text-sm font-medium text-[#191919]">
                    Delivery Price
                  </p>
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-[#FFEFEB] border border-r-0 border-[#FFCCBF] rounded-l-md text-sm">
                      {currencySymbol}
                    </span>
                    <input
                      type="number"
                      value={deliverPrice}
                      onChange={(e) => setDeliveryPrice(e.target.value)}
                      placeholder="5"
                      className="flex-1 px-3 py-2 border rounded-r-md outline-none text-sm"
                    />
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}

            <div className="flex justify-end w-full">
              <PrimaryBtn
                btnName="Update"
                clickEvent={onAddressUpdate}
                extraClass="w-full sm:w-auto px-10"
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default UpdatePickUpAddress;
